import {
	AfterViewInit,
	Directive,
	ElementRef,
	HostListener,
	inject,
	OnDestroy,
	output,
	Renderer2,
} from '@angular/core';

@Directive({
	selector: '[directiveMatTableResizeCols]',
})
export class MatTableResizeColsDirective implements AfterViewInit, OnDestroy {
	private el = inject(ElementRef);
	private renderer = inject(Renderer2);

	startX: number = 0;
	targetTh: HTMLElement | null = null;
	startWidth: number = 0;
	isResizing: boolean = false;
	private sortHeaders: HTMLElement[] = [];
	private isFixedTable = false;
	private destroyed = false;

	resizedColumns = output<Record<string, string>>();

	ngAfterViewInit() {
		setTimeout(() => {
			this.init();
		});
	}

	ngOnDestroy() {
		this.destroyed = true;
	}

	init() {
		if (this.el.nativeElement.classList.contains('!table-fixed')) {
			this.isFixedTable = true;
		}

		this.setDraggableTriggers();
		this.findSortHeaders();
		this.emitWidths();
	}

	private findSortHeaders() {
		const sortHeaderSelectors = ['[mat-sort-header]', '.mat-sort-header', 'th[mat-sort-header]'];

		this.sortHeaders = [];
		sortHeaderSelectors.forEach((selector) => {
			const elements = this.el.nativeElement.querySelectorAll(selector);
			elements.forEach((el: HTMLElement) => {
				if (!this.sortHeaders.includes(el)) {
					this.sortHeaders.push(el);
				}
			});
		});
	}

	/**
	 * Creates a div element to be used as a trigger for the drag event
	 * for each column in the table. Columns whose header carries the
	 * `data-no-resize` attribute are skipped and stay a fixed width.
	 */
	private setDraggableTriggers() {
		const cols = this.el.nativeElement.querySelectorAll('th');
		cols.forEach((col: HTMLElement) => {
			if (col.hasAttribute('data-no-resize')) {
				return;
			}
			const div = this.renderer.createElement('div');
			this.renderer.addClass(div, 'resize-handle');
			this.renderer.setStyle(div, 'width', '4px');
			this.renderer.setStyle(div, 'height', '100%');
			this.renderer.setStyle(div, 'position', 'absolute');
			this.renderer.setStyle(div, 'top', '0');
			this.renderer.setStyle(div, 'right', '0');
			this.renderer.setStyle(div, 'cursor', 'col-resize');
			this.renderer.setStyle(div, 'z-index', '10');
			this.renderer.appendChild(col, div);
		});
	}

	@HostListener('document:mousedown', ['$event'])
	onMouseDown(event: MouseEvent) {
		if (
			event.target instanceof HTMLDivElement &&
			event.target.classList.contains('resize-handle')
		) {
			this.isResizing = true;
			this.startX = event.pageX;
			this.targetTh = event.target.closest('th');

			const childDiv: HTMLDivElement | null | undefined = this.targetTh?.querySelector(
				'.mat-sort-header-container'
			);
			this.startWidth =
				!this.isFixedTable && childDiv
					? childDiv.offsetWidth
					: this.targetTh
						? this.targetTh.offsetWidth
						: 0;

			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			this.toggleSortingDisabled(true);
		}
	}

	@HostListener('document:click', ['$event'])
	onClick(event: MouseEvent) {
		if (this.isResizing) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
		}

		if (
			event.target instanceof HTMLDivElement &&
			event.target.classList.contains('resize-handle')
		) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
		}
	}

	/**
	 * If the table header has a sortable div, we need to resize that
	 * else we just need to resize the th!
	 * @param event
	 */
	@HostListener('document:mousemove', ['$event'])
	onMouseMove(event: MouseEvent) {
		if (!this.targetTh || !this.isResizing) {
			return;
		}

		const offset = event.pageX - this.startX;
		const newWidth = Math.max(this.startWidth + offset, 30);

		const childDiv: HTMLDivElement | null = this.targetTh.querySelector(
			'.mat-sort-header-container'
		);
		this.renderer.setStyle(
			!this.isFixedTable && childDiv ? childDiv : this.targetTh,
			'width',
			`${newWidth}px`
		);

		this.emitWidths();
	}

	@HostListener('document:mouseup')
	onMouseUp() {
		if (this.isResizing) {
			setTimeout(() => {
				this.toggleSortingDisabled(false);
			}, 150);
		}
		this.isResizing = false;
		this.targetTh = null;
	}

	private emitWidths() {
		if (this.destroyed) return;

		const widths: Record<string, string> = {};
		const ths = this.el.nativeElement.querySelectorAll('th[mat-header-cell]');

		ths.forEach((th: HTMLElement) => {
			const colName = Array.from(th.classList)
				.find((c) => c.startsWith('mat-column-'))
				?.replace('mat-column-', '');

			if (colName) {
				const width = th.getBoundingClientRect().width;
				widths[colName] = `${width}px`;
			}
		});
		this.resizedColumns.emit(widths);
	}

	private toggleSortingDisabled(disabled: boolean) {
		this.sortHeaders.forEach((header: HTMLElement) => {
			if (disabled) {
				this.renderer.setStyle(header, 'pointer-events', 'none');
				this.renderer.setAttribute(header, 'data-sort-disabled', 'true');
			} else {
				this.renderer.removeStyle(header, 'pointer-events');
				this.renderer.removeAttribute(header, 'data-sort-disabled');
			}
		});
	}
}
